import React, {
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { Link } from "react-router-dom";
import {
  ChevronRight,
  ArrowLeft,
  ShieldCheck,
  Mail,
  MapPin,
  Globe,
  Smartphone,
  ArrowRight,
} from "lucide-react";
import { InnovriddhiMark } from "../components/InnovriddhiMark";
import "../styles/legal.css";

import termsRaw from "../legal/terms-of-use.md?raw";
import privacyRaw from "../legal/privacy-policy.md?raw";
import aupRaw from "../legal/acceptable-use-policy.md?raw";

const SOURCES = {
  "privacy-policy": { raw: privacyRaw, title: "Privacy Policy", read: "8 min" },
  "terms-of-use": { raw: termsRaw, title: "Terms of Use", read: "12 min" },
  "acceptable-use-policy": {
    raw: aupRaw,
    title: "Acceptable Use Policy",
    read: "5 min",
  },
} as const;

export type LegalKey = keyof typeof SOURCES;

interface TocEntry {
  id: string;
  n: string;
  t: string;
}

export const LegalDocument: React.FC<{ documentKey: LegalKey }> = ({
  documentKey,
}) => {
  const { raw, title, read } = SOURCES[documentKey];
  const { toc, body } = useMemo(() => parseDoc(raw), [raw]);
  const [activeId, selectSection] = useScrollSpy(toc.map((s) => s.id));

  useEffect(() => {
    const graph = {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        {
          "@type": "ListItem",
          position: 1,
          name: "Home",
          item: "https://innovriddhi.com/",
        },
        {
          "@type": "ListItem",
          position: 2,
          name: "Legal centre",
          item: "https://innovriddhi.com/legal",
        },
        {
          "@type": "ListItem",
          position: 3,
          name: title,
          item: `https://innovriddhi.com/${documentKey}`,
        },
      ],
    };
    const el = document.createElement("script");
    el.type = "application/ld+json";
    el.textContent = JSON.stringify(graph);
    document.head.appendChild(el);
    return () => el.remove();
  }, [title, documentKey]);

  return (
    <div className="pc selection:bg-plum selection:text-white">
      <header className="pc__bar">
        <Link
          to="/"
          className="flex items-center gap-3 group focus:outline-none focus-visible:ring-2 focus-visible:ring-plum rounded-md"
        >
          <div className="text-plum group-hover:scale-105 transition-transform duration-200">
            <InnovriddhiMark size={34} form="interlaced" />
          </div>
          <span className="font-brand text-2xl font-bold tracking-tight text-ink">
            Innovriddhi
          </span>
        </Link>
        <div className="pc__right">
          <div className="pc__bread">
            <Link to="/legal">Legal centre</Link>
            <ChevronRight className="w-3.5 h-3.5 text-ink-muted" />
            <b>{title}</b>
          </div>
          <Link className="pc__back-link" to="/legal">
            <ArrowLeft className="w-4 h-4" /> All policies
          </Link>
        </div>
      </header>

      <div className="pc__body">
        <nav className="pc__toc" aria-label="On this page">
          <div className="pc__toc-label">On this page</div>
          {toc.map((s) => (
            <a
              key={s.id}
              href={`#${s.id}`}
              className={activeId === s.id ? "is-active" : ""}
              onClick={(e) => {
                e.preventDefault();
                selectSection(s.id);
                document
                  .getElementById(s.id)
                  ?.scrollIntoView({ behavior: "smooth" });
              }}
            >
              <span className="n">{s.n}</span>
              <span>{s.t}</span>
            </a>
          ))}
        </nav>

        <article className="pc__paper">
          <div className="dochead">
            <span className="dochead__eyebrow">Legal</span>
            <h1 className="dochead__title">{title}</h1>
            <div className="dochead__meta">
              <span>Last updated 15 Jul 2026</span>
              <span className="dot">&middot;</span>
              <span>{read} read</span>
            </div>
          </div>
          <div className="docprose">{body}</div>
        </article>
      </div>
    </div>
  );
};

/* Scroll spy: tracks section currently in view */
function useScrollSpy(ids: string[]): [string, (id: string) => void] {
  const [active, setActive] = useState("");
  const lockedRef = useRef(false);

  useEffect(() => {
    if (!ids.length) return;
    const els = ids
      .map((id) => document.getElementById(id))
      .filter(Boolean) as HTMLElement[];
    if (!els.length) return;
    const OFFSET = 100;

    let raf = 0;
    const compute = () => {
      raf = 0;
      if (lockedRef.current) return;
      const scrollBottom = window.scrollY + window.innerHeight;
      if (scrollBottom >= document.documentElement.scrollHeight - 2) {
        setActive(els[els.length - 1].id);
        return;
      }
      let current = els[0].id;
      for (const el of els) {
        if (el.getBoundingClientRect().top <= OFFSET) current = el.id;
        else break;
      }
      setActive(current);
    };
    const onScroll = () => {
      if (!raf) raf = requestAnimationFrame(compute);
    };
    const release = () => {
      lockedRef.current = false;
      onScroll();
    };

    compute();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll, { passive: true });
    window.addEventListener("wheel", release, { passive: true });
    window.addEventListener("touchmove", release, { passive: true });
    window.addEventListener("keydown", release);
    return () => {
      if (raf) cancelAnimationFrame(raf);
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
      window.removeEventListener("wheel", release);
      window.removeEventListener("touchmove", release);
      window.removeEventListener("keydown", release);
    };
  }, [ids.join("|")]);

  const select = (id: string) => {
    lockedRef.current = true;
    setActive(id);
  };
  return [active, select];
}

/* Minimal markdown -> { toc, body } */
function parseDoc(src: string): { toc: TocEntry[]; body: ReactNode[] } {
  const lines = src.split("\n");
  const toc: TocEntry[] = [];
  const body: ReactNode[] = [];
  let key = 0;
  let i = 0;

  while (i < lines.length) {
    const line = lines[i];
    key++;
    if (line.startsWith("# ") || /^Last Updated:/i.test(line.trim())) {
      // shown in the document header
    } else if (line.startsWith("## ")) {
      const text = line
        .slice(3)
        .replace(/^\d+\.\s*/, "")
        .trim();
      const n = String(toc.length + 1).padStart(2, "0");
      const id = `sec-${toc.length + 1}`;
      toc.push({ id, n, t: text });
      body.push(
        <h2 id={id} key={key}>
          <span className="doc-n">{n}</span>
          {inline(text)}
        </h2>,
      );
      // Contact section rendering
      if (/contact/i.test(text)) {
        const buf: string[] = [];
        i++;
        while (i < lines.length && !lines[i].startsWith("## ")) {
          buf.push(lines[i]);
          i++;
        }
        body.push(renderContact(buf, `contact-${key}`));
        continue;
      }
    } else if (line.startsWith("### ")) {
      body.push(<h3 key={key}>{inline(line.slice(4))}</h3>);
    } else if (line.startsWith("- ")) {
      const items: string[] = [];
      while (i < lines.length && lines[i].startsWith("- ")) {
        items.push(lines[i].slice(2));
        i++;
      }
      body.push(
        <ul key={key}>
          {items.map((t, j) => (
            <li key={j}>{inline(t)}</li>
          ))}
        </ul>,
      );
      continue;
    } else if (line.trim() !== "") {
      body.push(<p key={key}>{inline(line)}</p>);
    }
    i++;
  }
  return { toc, body };
}

/* Renders styled contact cards */
function renderContact(lines: string[], keyBase: string): ReactNode {
  let intro = "";
  let org = "";
  const fields: { label: string; value: string }[] = [];
  for (const raw of lines) {
    const line = raw.trim();
    if (!line) continue;
    const bold = line.match(/^\*\*(.+)\*\*$/);
    if (bold) {
      org = bold[1];
      continue;
    }
    const kv = line.match(/^([A-Za-z][\w\s]*?):\s*(.+)$/);
    if (kv) {
      fields.push({ label: kv[1].trim(), value: kv[2].trim() });
      continue;
    }
    intro = intro ? `${intro} ${line}` : line;
  }

  const renderIcon = (label: string) => {
    const l = label.toLowerCase();
    if (l.includes("security")) return <ShieldCheck className="w-4 h-4" />;
    if (l.includes("email") || l.includes("officer"))
      return <Mail className="w-4 h-4" />;
    if (l.includes("address")) return <MapPin className="w-4 h-4" />;
    if (l.includes("web")) return <Globe className="w-4 h-4" />;
    if (l.includes("phone") || l.includes("call"))
      return <Smartphone className="w-4 h-4" />;
    return <ArrowRight className="w-4 h-4" />;
  };

  const hrefFor = (value: string): string | null => {
    if (/^https?:\/\//i.test(value)) return value;
    if (/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) return `mailto:${value}`;
    return null;
  };

  return (
    <div className="doc-contact" key={keyBase}>
      {intro && <p className="doc-contact__intro">{intro}</p>}
      <div className="doc-contact__card">
        {org && <div className="doc-contact__org">{org}</div>}
        <div className="doc-contact__rows">
          {fields.map((f, idx) => {
            const wide = f.label.toLowerCase().includes("address");
            const cls = `doc-contact__row${wide ? " doc-contact__row--wide" : ""}`;
            const iconEl = (
              <span className="doc-contact__ic">{renderIcon(f.label)}</span>
            );
            const tokens = f.value
              .split(",")
              .map((s) => s.trim())
              .filter(Boolean);
            const linked = tokens.map((t) => ({ t, href: hrefFor(t) }));
            const urlCount = linked.filter((p) => p.href).length;

            if (tokens.length === 1 && linked[0].href) {
              const href = linked[0].href;
              const ext = href.startsWith("http");
              return (
                <a
                  key={idx}
                  className={cls}
                  href={href}
                  target={ext ? "_blank" : undefined}
                  rel={ext ? "noreferrer" : undefined}
                >
                  {iconEl}
                  <span className="doc-contact__txt">
                    <span className="doc-contact__k">{f.label}</span>
                    <span className="doc-contact__v">{f.value}</span>
                  </span>
                </a>
              );
            }

            const valueNode =
              urlCount >= 2
                ? linked.map((p, j) => (
                    <span key={j}>
                      {j > 0 && (
                        <span className="doc-contact__sep"> &middot; </span>
                      )}
                      {p.href ? (
                        <a
                          className="doc-contact__vlink"
                          href={p.href}
                          target={
                            p.href.startsWith("http") ? "_blank" : undefined
                          }
                          rel={
                            p.href.startsWith("http") ? "noreferrer" : undefined
                          }
                        >
                          {p.t.replace(/^https?:\/\//, "")}
                        </a>
                      ) : (
                        p.t
                      )}
                    </span>
                  ))
                : f.value;

            return (
              <div key={idx} className={cls}>
                {iconEl}
                <span className="doc-contact__txt">
                  <span className="doc-contact__k">{f.label}</span>
                  <span className="doc-contact__v">{valueNode}</span>
                </span>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function inline(text: string): ReactNode {
  const parts: ReactNode[] = [];
  let remaining = text;
  let k = 0;
  const linkRx = /\[([^\]]+)\]\(([^)]+)\)/;
  const boldRx = /\*\*([^*]+)\*\*/;
  while (remaining.length) {
    k++;
    const lm = remaining.match(linkRx);
    const bm = remaining.match(boldRx);
    const pick =
      lm && (!bm || lm.index! < bm.index!)
        ? { type: "link" as const, m: lm }
        : bm
          ? { type: "bold" as const, m: bm }
          : null;
    if (!pick) {
      parts.push(remaining);
      break;
    }
    const idx = pick.m.index!;
    if (idx > 0) parts.push(remaining.slice(0, idx));
    if (pick.type === "link") {
      parts.push(
        <a key={k} href={pick.m[2]} target="_blank" rel="noreferrer">
          {pick.m[1]}
        </a>,
      );
    } else {
      parts.push(<strong key={k}>{pick.m[1]}</strong>);
    }
    remaining = remaining.slice(idx + pick.m[0].length);
  }
  return <>{parts}</>;
}

export default LegalDocument;
